// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexCollectionGrid } from '@components/_rolex/CollectionGrid'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import { RolexPushBannerGrid } from '@components/_rolex/push-banner-grid'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { Item, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexCollectionPage } from '@lib/kontent/models/RolexCollectionPage'
import { RolexImage as RolexImageModel } from '@lib/kontent/models/RolexImage'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexPushBannerGrid as RolexPushBannerGridModel } from '@lib/kontent/models/RolexPushBannerGrid'
import { RolexWatchesPage as RolexWatchesPageModel } from '@lib/kontent/models/RolexWatchesPage'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC } from 'react'

type Props = SingleItemResponse<RolexWatchesPageModel>

const RolexWatchesPage: FC<Props> = ({ item, modular_content }) => {
  const { t } = useTranslation('rolex')
  const { locales, locale } = useRouter()
  const heroBanner = modular_content[item.elements.intro_image.modular_content[0]]
    .elements as RolexImageModel
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )
  const pageIntro = modular_content[item.elements.title___description.modular_content[0]]
    .elements as RolexPageIntroModel
  const featuredLinkGrid =
    item.elements.featured_link_grid__optional_.value[0] &&
    (modular_content[
      item.elements.featured_link_grid__optional_.value[0]
    ] as Item<RolexPushBannerGridModel>)

  const classicWatches = item.elements.classic_watches.value.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPage[]
  const professionalWatches = item.elements.professional_watches.value.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPage[]
  const perpetualWatches = item.elements.perpetual_watches.value.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPage[]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'rolex.watches', '/rolex')}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="collection page">
        <RolexBreadcrumbs items={[]} currentPageTitle={t('navigation.watches', { ns: 'rolex' })} />
        <div className="bg-rolexLightBeige">
          <RolexImage
            size="xl"
            mobileImage={heroBanner.mobile_image}
            desktopImage={heroBanner.desktop_image}
          />
          <RolexPageIntro
            title={pageIntro.title.value}
            description={pageIntro.description}
            titleTag={pageIntro.title_tag.value[0].codename}
          />
          {featuredLinkGrid && (
            <RolexPushBannerGrid
              {...featuredLinkGrid.elements}
              banners={featuredLinkGrid.elements.items.value.map(
                (key) => modular_content[key].elements,
              )}
            />
          )}
          <RolexCollectionGrid title={t('classicWatches')} items={classicWatches} />
          <RolexCollectionGrid title={t('professionalWatches')} items={professionalWatches} />
          <RolexCollectionGrid title={t('perpetualWatches')} items={perpetualWatches} />
          <KeepExploring className="bg-white py-[60px] lg:py-[90px]" items={keepExploringItems} />
        </div>
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexWatchesPageModel>({
      codename: 'rolex_watches_page',
      lang: language,
      preview,
    })

    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        ...content,
      },
    }
  } catch (error) {
    throw error
  }
}

export default RolexWatchesPage
